import { Seo } from "../components/Seo";

import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { WaitlistDialog } from "../components/WaitlistDialog";
import { ArrowLeft, Clock, ShoppingBag } from "lucide-react";
import { SHOPIFY_URL } from "../lib/config";

const typeLabel = { book: "Book", course: "Course", tool: "Tool" };

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/store"));
  }, [id, navigate]);

  if (!product)
    return <div className="min-h-[60vh] flex items-center justify-center text-forest">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="product-detail-page">
      <Seo
        title={product.title}
        description={product.tagline || product.description?.slice(0, 155) || product.title}
        path={`/store/${product.id}`}
        image={product.image_url?.startsWith("http") ? product.image_url : undefined}
        type="product"
      />
      <Link to="/store" className="inline-flex items-center gap-2 text-charcoal/60 hover:text-forest mb-8 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to store
      </Link>

      <div className="grid lg:grid-cols-2 gap-14 items-start">
        <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/5] sticky top-28 bg-ivory flex items-center justify-center">
          <img
            src={product.image_url || "https://images.unsplash.com/photo-1784032514423-9d700799a19a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"}
            alt={product.title}
            className={product.image_url?.includes("book-cover") ? "w-full h-full object-contain p-2" : "w-full h-full object-cover"}
          />
        </div>

        <div>
          <span className="inline-block bg-white border border-gold/40 text-forest text-xs uppercase tracking-widest px-3 py-1 rounded-full">
            {typeLabel[product.type] || product.type}
          </span>
          <h1 className="font-serif text-5xl text-forest mt-5 leading-tight">{product.title}</h1>
          <p className="text-charcoal/70 text-lg mt-4 italic">{product.tagline}</p>

          {product.coming_soon ? (
            <div className="mt-8">
              <div className="text-3xl text-charcoal font-medium" data-testid="detail-price">${product.price.toFixed(2)}</div>
              <div className="inline-flex items-center gap-2 mt-4 bg-gold/15 border border-gold/40 rounded-full px-5 py-2" data-testid="coming-soon-pill">
                <span className="w-2 h-2 rounded-full bg-gold" />
                <span className="text-forest font-medium">{product.release_note || "Coming Soon"}</span>
              </div>
            </div>
          ) : (
            <div className="text-3xl text-charcoal font-medium mt-8">${product.price.toFixed(2)}</div>
          )}

          <p className="text-charcoal/70 leading-relaxed mt-8">{product.description}</p>

          <div className="mt-10">
            {product.coming_soon ? (
              <div className="flex flex-col sm:flex-row gap-4">
                <Button disabled className="rounded-full bg-forest/20 text-forest px-8 h-12 gap-2 cursor-not-allowed" data-testid="coming-soon-btn">
                  <Clock className="w-5 h-5" /> Coming Soon
                </Button>
                <WaitlistDialog source={`product:${product.title}`}>
                  <Button
                    variant="outline"
                    className="rounded-full border-forest/30 text-forest hover:bg-forest/5 px-8 h-12"
                    data-testid="notify-me-btn"
                  >
                    Join the Community for Launch Updates
                  </Button>
                </WaitlistDialog>
              </div>
            ) : (
              <a href={SHOPIFY_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  className="rounded-full bg-forest hover:bg-forest-light text-ivory px-10 h-13 py-6 text-base transition-colors gap-2"
                  data-testid="buy-on-shopify-btn"
                >
                  <ShoppingBag className="w-5 h-5" /> Buy on Shopify
                </Button>
              </a>
            )}
          </div>
          <p className="text-charcoal/50 text-sm mt-5">
            {product.coming_soon
              ? "This title isn't available for purchase just yet — check back on launch day."
              : "You'll be taken to our secure Shopify shop to complete your purchase."}
          </p>
        </div>
      </div>
    </div>
  );
}
